import { useState } from 'react';
import { Process, ProcessCreate, ProcessUpdate } from '../types/costwand.d';
import { useProcesses } from '../hooks/useProcesses.ts';
import { useCurrencyLookup } from '../hooks/useCurrencyLookup.ts'; // Import the currency lookup hook
import ProcessForm from '../components/ProcessForm.tsx';
import ProcessTable from '../components/ProcessTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  // DialogFooter, // Removed DialogFooter as it's not used for the form anymore
} from '../components/ui/dialog.tsx';
// import { useError } from '../context/ErrorContext.tsx'; // Not needed as apiClient handles global error

const ProcessesPage = () => {
  const {
    processes,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createProcess,
    updateProcess,
    deleteProcess,
    fetchProcesses,
  } = useProcesses();

  const { currencies, loading: currenciesLoading, error: currenciesError } = useCurrencyLookup();

  // const { displayError } = useError(); // Not needed as apiClient handles global error

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingProcess, setEditingProcess] = useState<Process | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  // Removed states for the separate error dialog for form submissions
  // const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  // const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

  const handleAddProcess = () => {
    setEditingProcess(null);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleEditProcess = (process: Process) => {
    setEditingProcess(process);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleDeleteProcess = async (id: number) => {
    await deleteProcess(id);
    fetchProcesses(currentPage);
  };

  const handleFormSubmit = async (data: ProcessCreate | ProcessUpdate) => {
    setFormSubmitting(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);

    try {
      let success = false;
      if (editingProcess) {
        // Cast data to ProcessUpdate as it's an update operation
        success = await updateProcess(editingProcess.id!, data as ProcessUpdate);
      } else {
        success = await createProcess(data as ProcessCreate);
      }

      if (success) {
        setIsFormOpen(false);
        fetchProcesses(currentPage);
      } else {
        // The global error handler in apiClient would have already displayed the error.
        // We just need to ensure the form stays open if it failed.
        // displayError(error || "An unexpected error occurred during form submission."); // If we wanted to explicitly trigger global error here
        // setIsErrorDialogOpen(true); // Removed local error dialog trigger
      }
    } catch (e: any) {
      // The global error handler in apiClient would have already displayed the error.
      // displayError(e.message || "An unexpected error occurred."); // If we wanted to explicitly trigger global error here
      // setIsErrorDialogOpen(true); // Removed local error dialog trigger
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingProcess(null);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  // Removed handleCloseErrorDialog function

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Process Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddProcess} disabled={currenciesLoading}>Add New Process</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingProcess ? 'Edit Process' : 'Add New Process'}</DialogTitle>
              <DialogDescription>
                {editingProcess ? 'Make changes to the process here.' : 'Add a new process to your list.'}
              </DialogDescription>
            </DialogHeader>
            {currenciesError && (
              <p className="text-destructive text-sm p-2 bg-red-100 border border-red-400 rounded">
                Error loading currencies: {currenciesError}
              </p>
            )}
            <ProcessForm
              initialData={editingProcess}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              currencies={currencies}
              lookupLoading={currenciesLoading}
              // error={errorDialogMessage} // Removed the error prop
            />
          </DialogContent>
        </Dialog>
      </div>

      <ProcessTable
        processes={processes}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditProcess}
        onDelete={handleDeleteProcess}
        loading={loading}
        error={error}
        baseEndpoint="/processes" // NEW: Pass the base endpoint
      />

      {/* Removed Error Dialog for form submission errors */}
      {/*
      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Error</DialogTitle>
            <DialogDescription>
              An error occurred during the operation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-red-600">
            {errorDialogMessage || 'An unexpected error occurred.'}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default ProcessesPage;